import type { TrailerImageName } from "../../services/yard.service";

export interface TrailerAssignment {
    parkingSpaceNumber: string;
    trailerNumber: string;
    trailerType: string;
    trailerLength: 28 | 53;
    trailerSkirts: 'yes' | 'no' | null;
    imageName: TrailerImageName;
    slotIndex: number;
    slotCount: number;
}

function normalizeSkirts(value: unknown): 'yes' | 'no' | null {
    const text = String(value ?? "").trim().toLowerCase();
    return text === "yes" || text === "no" ? text : null;
}

function normalizeLength(value: unknown): 28 | 53 {
    const text = String(value ?? "53").trim();
    return text.startsWith("28") ? 28 : 53;
}

function normalizeType(value: unknown): string {
    const text = String(value ?? "dry van")
        .trim()
        .toLowerCase()
        .replace(/[_-]/g, " ");

    if (text.includes("reefer") || text.includes("refrigerated")) {
        return "reefer";
    }

    if (text.includes("intermodal") || text.includes("container")) {
        return "intermodal";
    }

    return "dry van";
}

function imageFor(
    type: string,
    length: 28 | 53
): TrailerImageName {
    return `${length} foot ${type}.png` as TrailerImageName;
}

export function getTrailerAssignments(
    records: Record<string, unknown>[]
): TrailerAssignment[] {
    const assignments: TrailerAssignment[] = [];

    for (const record of records) {
        const space = String(
            record.parking_space_number ?? ""
        ).trim();

        if (!space) continue;

        const firstNumber = String(
            record.trailer_number ?? ""
        ).trim();

        const secondNumber = String(
            record.trailer_number_2 ?? ""
        ).trim();

        if (!firstNumber && !secondNumber) continue;

        const firstLength = normalizeLength(record.trailer_length);
        const secondLength = normalizeLength(record.trailer_length_2);

        const useTwo =
            Boolean(firstNumber) &&
            Boolean(secondNumber) &&
            firstLength === 28 &&
            secondLength === 28;

        const selected = useTwo
            ? [
                {
                    number: firstNumber,
                    type: record.trailer_type,
                    length: firstLength,
                    skirts: record.trailer_skirts,
                    slotIndex: 1
                },
                {
                    number: secondNumber,
                    type: record.trailer_type_2,
                    length: secondLength,
                    skirts: record.trailer_skirts_2,
                    slotIndex: 0
                }
            ]
            : [{
                number: firstNumber || secondNumber,
                type: firstNumber
                    ? record.trailer_type
                    : record.trailer_type_2,
                length: firstNumber
                    ? firstLength
                    : secondLength,
                skirts: firstNumber
                    ? record.trailer_skirts
                    : record.trailer_skirts_2,
                slotIndex: 0
            }];

        for (const trailer of selected) {
            const type = normalizeType(trailer.type);

            assignments.push({
                parkingSpaceNumber: space,
                trailerNumber: trailer.number,
                trailerType: type,
                trailerLength: trailer.length,
                trailerSkirts: normalizeSkirts(trailer.skirts),
                imageName: imageFor(type, trailer.length),
                slotIndex: trailer.slotIndex,
                slotCount: useTwo ? 2 : 1
            });
        }
    }

    return assignments;
}
